import { allCommands, createGroup } from "app/client/components/commands";
import { GristDoc } from "app/client/components/GristDoc";
import { makeTestId } from "app/client/lib/domUtils";
import { makeT } from "app/client/lib/localization";
import { inlineMarkdown } from "app/client/lib/markdown";
import { logTelemetryEvent } from "app/client/lib/telemetry";
import { ChatHistory } from "app/client/models/ChatHistory";
import { reportError } from "app/client/models/errors";
import { IAssistantPanel, IWidgetCreator } from "app/client/ui/IAssistantPanel";
import {
  cssKeyboardShortcut,
  cssLinkText,
  cssPageButton,
  cssPageEntry,
  cssPageIcon,
  cssTools,
} from "app/client/ui/LeftPanelCommon";
import { hoverTooltip } from "app/client/ui/tooltips";
import { theme } from "app/client/ui2018/cssVars";
import { editableLabel } from "app/client/ui2018/editableLabel";
import { icon } from "app/client/ui2018/icons";
import { cssLink } from "app/client/ui2018/links";
import { loadingSpinner } from "app/client/ui2018/loaders";
import { Assistant, cssAiImage, cssAvatar } from "app/client/widgets/Assistant";
import { assistantHistoryMenu } from "app/client/widgets/AssistantHistoryMenu";
import { AssistantSessionStore } from "app/client/widgets/AssistantSessionStore";
import {
  cssAiIntroMessage,
  cssAiMessageParagraph,
} from "app/client/widgets/FormulaAssistant";
import { AssistantState } from "app/common/ActiveDocAPI";
import {
  AssistanceMessage, AssistanceState, AssistanceToolCall, AssistantDraftRequest,
  CreatorBrowserToolName, DeveloperPromptVersion,
} from "app/common/Assistance";
import { commonUrls } from "app/common/gristUrls";
import { isLongerThan, safeJsonParse, waitGrainObs } from "app/common/gutil";
import { TelemetryEvent, TelemetryMetadata } from "app/common/Telemetry";
import { getGristConfig } from "app/common/urlUtils";
import { WidgetType } from "app/common/widgetTypes";

import {
  Computed,
  Disposable,
  dom,
  DomContents,
  DomElementArg,
  MultiHolder,
  Observable,
  styled,
  subscribe,
} from "grainjs";
import findLastIndex from "lodash/findLastIndex";

// eslint-disable-next-line local/makeT-filename
const t = makeT("Assistant");

const testId = makeTestId("test-assistant-");

const ASSISTANT_TOOLTIP_KEY = "assistantToolbarTooltip";

const DELAY_BEFORE_SPINNER_MS = 500;

/**
 * The {@link Assistant} and DOM of a single session.
 */
interface SessionView {
  owner: MultiHolder;
  assistant: Assistant;
  history: Observable<ChatHistory>;
  elem: HTMLElement;
  /** Number of requests awaiting a response. The view is kept alive until there are none left. */
  pending: number;
}

// How many times one message may send tool results back to the AI.
const MAX_TOOL_ROUNDS = 20;

export function buildAssistantPanel(gristDoc: GristDoc): IAssistantPanel | null {
  const { assistant } = getGristConfig();
  if (!assistant || assistant.version === 1) {
    return null;
  }

  return AssistantPanel.create(gristDoc, gristDoc);
}

class AssistantPanel extends Disposable implements IAssistantPanel {
  public readonly isOpen = Computed.create(this, use => use(this._gristDoc.docPageModel.innerLeftPanelOpen));

  private _store = AssistantSessionStore.create(this, this._gristDoc);

  private _isLoading = Computed.create(this, use =>
    !use(this._store.isLoaded) || use(this._store.isActiveSessionLoading));

  /** Whether loading has taken long enough to show a spinner. */
  private _isLoadingSlow = Observable.create(this, false);

  private _composerCommands = this.autoDispose(createGroup({
    toggleAssistant: () => this.toggle(),
  }, this, false));

  /**
   * Views by session id. Only the active session's view is attached to `_sessionHost`; others are
   * kept while their responses are pending, so sessions keep running in the background.
   */
  private _views = new Map<string, SessionView>();
  private _sessionHost: HTMLElement = cssSessionHost();
  private _headerElem: HTMLElement | undefined;
  private _panelElem: HTMLElement | undefined;

  constructor(private _gristDoc: GristDoc) {
    super();

    this.autoDispose(subscribe(this._store.activeSessionId, this._isLoading, () => this._showActiveView()));
    this.autoDispose(this._isLoading.addListener(() => this._watchLoading()));
    this.onDispose(() => {
      for (const id of [...this._views.keys()]) { this._disposeView(id); }
    });
  }

  public open() {
    allCommands.innerLeftPanelOpen.run();
    this._store.load().then(() => this._focusComposer()).catch(reportError);
    this._logTelemetryEvent("assistantOpen");
  }

  public close() {
    allCommands.innerLeftPanelClose.run();
  }

  public toggle() {
    if (!this.isOpen.get()) {
      this.open();
    } else if (this._hasFocus()) {
      this._closeAndFocusSection();
    } else {
      this._focusComposer();
    }
  }

  public ask(prompt: string, developerPromptVersion?: DeveloperPromptVersion) {
    this._askInNewSession(prompt, developerPromptVersion ?? "default").catch(reportError);
    this.open();
  }

  public setState({ prompt, developerPromptVersion = "new-document" }: AssistantState) {
    this._askInNewSession(prompt, developerPromptVersion).catch(reportError);
  }

  public buildDom(): DomContents {
    this._watchLoading();
    this._store.load().catch(reportError);
    return [
      this._buildHeader(),
      this._buildBody(),
    ];
  }

  private _buildHeader() {
    return cssPanelHeader(
      (elem: HTMLElement) => { this._headerElem = elem; },
      cssPanelHeaderIcon("Sparkle"),
      dom.domComputed(use => use(this._store.activeSession)?.id ?? null, id =>
        id ? this._buildEditableTitle(id) : cssPanelHeaderTitle(
          cssTitleBox(
            cssTitleLabel(t("New session")),
            cssTitlePencilSpace(),
            testId("panel-title"),
          ),
        ),
      ),
      cssPanelToolbar(
        cssToolbarButton(
          icon("Clock"),
          assistantHistoryMenu(this._store, { onDelete: id => this._deleteSession(id) }),
          hoverTooltip(t("Session history"), {
            key: ASSISTANT_TOOLTIP_KEY,
          }),
          testId("history"),
        ),
        cssToolbarButton(
          icon("Plus"),
          dom.on("click", () => this._store.newSession()),
          hoverTooltip(t("New session"), { key: ASSISTANT_TOOLTIP_KEY }),
          testId("new-session"),
        ),
        cssToolbarButton(
          icon("CrossBig"),
          dom.on("click", () => this.close()),
          hoverTooltip(t("Close"), { key: ASSISTANT_TOOLTIP_KEY }),
          testId("panel-close"),
        ),
      ),
      testId("panel-header"),
    );
  }

  private _buildBody() {
    return cssAssistantPanel(
      testId("panel"),
      (elem: HTMLElement) => { this._panelElem = elem; },
      dom.onKeyDown({
        Escape: (ev) => {
          ev.stopPropagation();
          this._closeAndFocusSection();
        },
      }),
      dom.maybe(this._isLoadingSlow, () => cssSpinnerWrapper(loadingSpinner(), testId("session-loading"))),
      this._sessionHost,
    );
  }

  private _closeAndFocusSection() {
    this.close();
    this._gristDoc.regionFocusSwitcher?.focusActiveSection();
  }

  private _hasFocus() {
    const active = document.activeElement;
    return Boolean(this._headerElem?.contains(active) || this._panelElem?.contains(active));
  }

  /**
   * Shows a spinner if loading takes longer than {@link DELAY_BEFORE_SPINNER_MS}.
   */
  private _watchLoading() {
    this._isLoadingSlow.set(false);
    if (!this._isLoading.get()) { return; }

    const loaded = waitGrainObs(this._isLoading, isLoading => !isLoading);
    isLongerThan(loaded, DELAY_BEFORE_SPINNER_MS).then((isSlow) => {
      if (isSlow && !this.isDisposed() && this._isLoading.get()) { this._isLoadingSlow.set(true); }
    }).catch(reportError);
  }

  /**
   * Starts a new session with `developerPromptVersion`, and sends `prompt` in it.
   */
  private async _askInNewSession(prompt: string, developerPromptVersion: DeveloperPromptVersion) {
    await this._store.load();
    if (this.isDisposed()) { return; }

    this._store.newSession();
    const view = this._showActiveView();
    if (!view) { return; }

    view.history.set({ ...view.history.get(), developerPromptVersion });
    await view.assistant.send(prompt);
  }

  private _focusComposer() {
    this._showActiveView()?.assistant.focus();
  }

  /**
   * Shows the active session's view, creating it if needed, and disposes views of other sessions
   * that have no pending requests.
   *
   * Returns null (and shows nothing) while sessions are loading.
   */
  private _showActiveView(): SessionView | null {
    if (this.isDisposed()) { return null; }

    const id = this._store.activeSessionId.get();
    this._disposeIdleViews();
    if (this._isLoading.get()) {
      this._sessionHost.replaceChildren();
      return null;
    }

    const view = this._views.get(id) ?? this._createView(id);
    if (view.elem.parentElement !== this._sessionHost) {
      this._sessionHost.replaceChildren(view.elem);
      view.assistant.scrollToBottom({ sync: true });
    }
    return view;
  }

  private _createView(id: string): SessionView {
    const owner = MultiHolder.create(null);
    const history = Observable.create<ChatHistory>(owner, this._store.getHistory(id));
    const assistant = Assistant.create(owner, {
      history,
      gristDoc: this._gristDoc,
      onSend: async (message) => {
        view.pending++;
        try {
          return await this._sendMessage(id, history.get(), message);
        } finally {
          view.pending--;
          setTimeout(() => this._disposeIdleViews(), 0);
        }
      },
      onEscape: () => this._closeAndFocusSection(),
      onNewSession: () => this._store.newSession(),
      buildIntroMessage,
    });
    owner.autoDispose(history.addListener(value => this._store.saveHistory(id, value)));
    const elem = cssSessionView(
      assistant.buildDom(),
      (el: HTMLElement) => {
        const input = el.querySelector("textarea");
        if (input) { this._composerCommands.attach(input); }
      },
    );
    const view: SessionView = { owner, assistant, history, elem, pending: 0 };
    this._views.set(id, view);
    return view;
  }

  private _disposeIdleViews() {
    if (this.isDisposed()) { return; }

    const activeId = this._store.activeSessionId.get();
    for (const [id, view] of this._views) {
      if (id !== activeId && view.pending === 0) { this._disposeView(id); }
    }
  }

  private _deleteSession(id: string) {
    this._store.deleteSession(id);
    this._disposeView(id);
  }

  private _disposeView(id: string) {
    const view = this._views.get(id);
    if (!view) { return; }

    this._views.delete(id);
    view.elem.remove();
    dom.domDispose(view.elem);
    view.owner.dispose();
  }

  private _buildEditableTitle(id: string): DomContents {
    const title = Computed.create(null, use =>
      use(this._store.sessions).find(session => session.id === id)?.title ?? t("New session"));

    return cssPanelHeaderTitle(
      dom.autoDispose(title),
      cssPanelHeaderTitle.cls("-editable"),
      cssTitleBox(
        dom.on("mousedown", (ev, elem) => {
          const input = elem.querySelector("input");
          if (!input || ev.target === input) { return; }

          ev.preventDefault();
          input.focus();
        }),
        cssTitleLabel(
          editableLabel(title, {
            save: (value: string) => this._store.renameSession(id, value),
            args: [cssTitleLabelWrap.cls("")],
            inputArgs: [cssTitleInput.cls(""), testId("panel-title-input")],
          }),
        ),
        cssTitlePencilSpace(cssTitlePencil("Pencil")),
        dom.attr("title", use => use(title)),
        testId("panel-title"),
      ),
    );
  }

  // The AI answers with a reply, or with tool calls for the browser. The browser runs them and sends
  // their results back, until the AI replies.
  private async _sendMessage(conversationId: string, history: ChatHistory, message: string) {
    const { developerPromptVersion = "default", state } = history;
    const ask = (request: { text: string, state?: AssistanceState } | { state: AssistanceState }) =>
      askAI(this._gristDoc, { ...request, conversationId, developerPromptVersion });
    let response = await ask({ text: message, state });
    for (let round = 0; ; round++) {
      // The server runs its own tools, and leaves the ones for the browser without an answer.
      const calls = getUnansweredToolCalls(response.state);
      if (!calls.length) { return response; }
      if (round === MAX_TOOL_ROUNDS) {
        throw new Error(t("There was a problem fulfilling your request. Please try again."));
      }
      response = await ask({ state: this._addToolResults(response.state!, calls) });
    }
  }

  // Runs the tool calls the server left for the browser, and adds their results to the state.
  private _addToolResults(state: AssistanceState, calls: AssistanceToolCall[]): AssistanceState {
    const results: AssistanceMessage[] = calls.map(({ id, function: { name, arguments: args } }) => {
      let result: unknown;
      try {
        result = this._executeOneClientTool(CreatorBrowserToolName.check(name), safeJsonParse(args, {}));
      } catch (e) {
        result = { ok: false, error: String(e) };
      }
      return { role: "tool", tool_call_id: id, content: JSON.stringify(result) };
    });
    return { ...state, messages: [...state.messages ?? [], ...results] };
  }

  private _executeOneClientTool(name: CreatorBrowserToolName, params: { widget_id: number; html?: string }): unknown {
    const view = this._findWidgetCreatorView(params.widget_id);
    switch (name) {
      case "get_creator_html": {
        return { html: view ? view.currentHtml() : null };
      }
      case "set_creator_html": {
        if (!view) {
          throw new Error(`Widget ${params.widget_id} is not on the page the user has open. ` +
            "Save it with draft: false, or ask the user to open its page.");
        }
        if (typeof params.html !== "string") { throw new Error("html must be a string"); }
        view.applyHtml(params.html);
        return {
          ok: true,
          note: "The widget runs this code in the user's editor. The user must click Save to keep it.",
        };
      }
    }
  }

  private _findWidgetCreatorView(sectionId: number): IWidgetCreator | null {
    const section = this._gristDoc.docModel.viewSections.getRowModel(sectionId);
    if (section.parentKey.peek() !== WidgetType.Creator) { return null; }
    return section.viewInstance.peek() as unknown as IWidgetCreator | null;
  }

  private _logTelemetryEvent(
    event: TelemetryEvent,
    metadata: TelemetryMetadata = {},
  ) {
    logTelemetryEvent(event, {
      full: {
        version: 2,
        docIdDigest: this._gristDoc.docId(),
        conversationId: this._store.activeSessionId.get(),
        context: {
          viewId: this._gristDoc.activeViewId.get(),
        },
        ...metadata,
      },
    });
  }
}

export function buildOpenAssistantButton(gristDoc: GristDoc, ...args: DomElementArg[]) {
  const assistant = gristDoc.assistantPanel;
  if (!assistant) { return null; }

  return cssPageEntry(
    cssPageButton(
      cssSparkleDisc(
        cssSparkleDisc.cls("-active", assistant.isOpen),
        cssSparklePageIcon("Sparkle"),
      ),
      cssLinkText(t("Assistant")),
      allCommands.toggleAssistant?.humanKeys.length ?
        cssAssistantShortcut(
          allCommands.toggleAssistant.humanKeys[0],
          testId("tools-shortcut-keys"),
        ) : null,
      dom.cls("tour-assistant"),
      dom.on("click", () => {
        if (assistant.isOpen.get()) {
          assistant.close();
        } else {
          assistant.open();
        }
      }),
      ...args,
    ));
}

// The tool calls of the last model message that have no answer yet.
function getUnansweredToolCalls(state: AssistanceState | undefined): AssistanceToolCall[] {
  const messages = state?.messages ?? [];
  const lastIndex = findLastIndex(messages, message => message.role === "assistant");
  const answered = new Set(messages.slice(lastIndex + 1).map(message => message.tool_call_id));
  return (messages[lastIndex]?.tool_calls ?? []).filter(call => !answered.has(call.id));
}

async function askAI(
  grist: GristDoc,
  request: AssistantDraftRequest,
) {
  const viewId = grist.activeViewId.get();
  const activeSectionId = grist.viewModel.activeSectionId.peek();
  return await grist.docComm.getAssistance({
    ...request,
    context: {
      viewId: typeof viewId === "number" ? viewId : undefined,
      activeSectionId: activeSectionId || undefined,
    },
  });
}

function buildIntroMessage(...args: DomElementArg[]) {
  return cssAiIntroMessage(
    cssAvatar(cssAiImage()),
    dom("div",
      cssAiMessageParagraph(t("Hi, I'm the Grist AI Assistant.")),
      cssAiMessageParagraph(
        t("Some things you should know when working with me:"),
      ),
      cssAiMessageList(
        cssAiMessageListItem(
          inlineMarkdown(
            t(
              "I **can** answer questions about your data, create or modify " +
              "tables, pages, and most widgets. I can link widgets and help " +
              "with formulas. I also know which page you're viewing.",
            ),
          ),
        ),
        cssAiMessageListItem(
          inlineMarkdown(
            t(
              "I **can't** create or modify charts, forms, access rules, or " +
              "modify page layout. I also don't know if you've selected something.",
            ),
          ),
        ),
        cssAiMessageListItem(
          t(
            'Talk to me like a person. For example, "What were the top ' +
            '3 months for gross revenue in the last year?"',
          ),
        ),
        getGristConfig().assistant?.provider === "OpenAI" ?
          cssAiMessageListItem(
            dom("div",
              t(
                "When you talk to me, your questions, document structure, " +
                "and data are sent to OpenAI. {{learnMore}}",
                {
                  learnMore: cssLink(t("Learn more."), {
                    href: commonUrls.helpAssistantDataUse,
                    target: "_blank",
                  }),
                },
              ),
            ),
          ) :
          null,
      ),
    ),
    ...args,
  );
}

const cssAssistantPanel = styled("div", `
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  overflow: hidden;
`);

const cssPanelHeader = styled("div", `
  height: 49px;
  flex: none;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 0 10px 0 8px;
  column-gap: 4px;
`);

const cssPanelHeaderIcon = styled(icon, `
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  --icon-color: ${theme.controlFg};
`);

const cssPanelHeaderTitle = styled("div", `
  display: flex;
  align-items: center;
  min-width: 0;
  color: ${theme.lightText};
  font-weight: 500;
`);

const cssTitleBox = styled("div", `
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 7px 8px;
  border-radius: 6px;
  outline: 1px solid transparent;

  .${cssPanelHeaderTitle.className}-editable & {
    cursor: text;
  }
  .${cssPanelHeaderTitle.className}-editable:hover & {
    background-color: ${theme.lightHover};
  }
  .${cssPanelHeaderTitle.className}-editable &:focus-within {
    background-color: transparent;
    outline-color: ${theme.controlFg};
  }
`);

const cssTitleLabel = styled("div", `
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`);

const cssTitleLabelWrap = styled("div", `
  max-width: 100%;
`);

const cssTitleInput = styled("input", `
  text-overflow: ellipsis;

  &:focus {
    text-overflow: clip;
  }
`);

const cssTitlePencilSpace = styled("div", `
  display: flex;
  align-items: center;
  flex: none;
  width: 13px;
  height: 13px;
  margin-left: 6px;
`);

const cssTitlePencil = styled(icon, `
  width: 13px;
  height: 13px;
  --icon-color: ${theme.lightText};
  visibility: hidden;

  .${cssTitleBox.className}:hover & {
    visibility: visible;
  }
  .${cssTitleBox.className}:focus-within & {
    visibility: hidden;
  }
`);

const cssPanelToolbar = styled("div", `
  display: flex;
  align-items: center;
  column-gap: 6px;
  margin-left: auto;
`);

const cssAssistantShortcut = styled(cssKeyboardShortcut, `
  .${cssTools.className}-collapsed & {
    display: none;
  }
`);

const cssSparkleDisc = styled("div", `
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 24px;
  height: 24px;
  margin-left: -3px;
  margin-right: calc(var(--page-icon-margin, 8px) - 3px);
  border-radius: 50%;

  &-active {
    background-color: ${theme.activePageBg};
    --icon-color: ${theme.activePageFg};
  }
`);

const cssSparklePageIcon = styled(cssPageIcon, `
  width: 18px;
  height: 18px;
  margin-right: 0;
`);

const cssToolbarButton = styled("div", `
  --icon-color: ${theme.controlSecondaryFg};
  border-radius: 3px;
  padding: 3px;
  cursor: pointer;
  user-select: none;

  &:hover, &.weasel-popup-open {
    background-color: ${theme.hover};
  }
`);

const cssSpinnerWrapper = styled("div", `
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  row-gap: 8px;
  text-align: center;
  color: ${theme.text};
  background-color: ${theme.mainPanelBg};
  z-index: 1;
`);

const cssAiMessageList = styled("ul", `
  margin-bottom: 8px;
`);

const cssAiMessageListItem = styled("li", `
  margin-bottom: 8px;
`);

const cssSessionHost = styled("div", `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
`);

const cssSessionView = styled("div", `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  overflow: hidden;
`);
